import React, { useState, useEffect, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import GroupWork from '@material-ui/icons/GroupWork';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from '@material-ui/core';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { FieldValidation } from '@rjsf/utils';

interface GitHubTeam {
  slug: string;
  name: string;
  description?: string;
}

/**
 * Multi-select picker for GitHub teams, backed by the live org team list so it
 * never goes stale as projects (and therefore teams) are created.
 *
 * The field value is an array of team slugs, which is what the GitHub team
 * membership API takes directly.
 */
export const validateGitHubTeams = (
  value: string[],
  validation: FieldValidation,
) => {
  if (!value || !Array.isArray(value) || value.length === 0) {
    validation.addError('Please select at least one GitHub team');
  }
};

export const CustomGitHubTeamsField = ({
  onChange,
  required,
  formData,
  schema,
  rawErrors,
  uiSchema,
}: FieldExtensionComponentProps<string[]>) => {
  const [teams, setTeams] = useState<GitHubTeam[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const config = useApi(configApiRef);

  const backendUrl = config.getString('backend.baseUrl');
  const orgOption = uiSchema?.['ui:options']?.org as string | undefined;

  const { title, description, placeholder, helperText } = schema;

  const selected = useMemo(
    () => (Array.isArray(formData) ? formData : []),
    [formData],
  );

  useEffect(() => {
    const fetchTeams = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const query = orgOption ? `?org=${encodeURIComponent(orgOption)}` : '';
        const response = await fetch(
          `${backendUrl}/api/planner/github-teams${query}`,
        );
        if (!response.ok) {
          throw new Error('Failed to fetch GitHub teams');
        }
        const data = await response.json();
        setTeams(data.teams || []);
      } catch (_) {
        setLoadError(
          'Could not load the GitHub team list. Please retry, or contact the DevHub platform team.',
        );
      } finally {
        setLoading(false);
      }
    };
    fetchTeams();
  }, [backendUrl, orgOption]);

  // Keep a lookup so selected slugs still render a friendly name.
  const nameBySlug = useMemo(() => {
    const map = new Map<string, string>();
    teams.forEach(team => map.set(team.slug, team.name));
    return map;
  }, [teams]);

  const selectedTeams = useMemo(
    () =>
      selected.map(
        slug => teams.find(team => team.slug === slug) || { slug, name: slug },
      ),
    [selected, teams],
  );

  return (
    <Box>
      <Autocomplete
        multiple
        disableCloseOnSelect
        loading={loading}
        options={teams}
        value={selectedTeams as GitHubTeam[]}
        getOptionLabel={option => option.name || option.slug}
        isOptionEqualToValue={(option, value) => option.slug === value.slug}
        onChange={(_event, newValue) => {
          onChange(newValue.map(team => team.slug));
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.slug}>
            <Box>
              <Box sx={{ fontWeight: 500 }}>{option.name}</Box>
              <Box sx={{ fontSize: '0.75rem', opacity: 0.7 }}>
                {option.slug}
                {option.description ? ` — ${option.description}` : ''}
              </Box>
            </Box>
          </li>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.slug}
              label={nameBySlug.get(option.slug) || option.slug}
              size="small"
            />
          ))
        }
        renderInput={params => (
          <TextField
            {...params}
            label={title}
            required={required}
            // MUI puts the native `required` attribute on the inner text input.
            // With multiple selection that input is always empty once chips are
            // rendered, so native validation blocks submit even when teams are
            // selected. validateGitHubTeams enforces the requirement instead.
            inputProps={{ ...params.inputProps, required: false }}
            placeholder={
              selected.length === 0
                ? (placeholder as string) || 'Search and select one or more teams'
                : ''
            }
            variant="outlined"
            margin="normal"
            error={Boolean(rawErrors?.length) || Boolean(loadError)}
            helperText={
              loadError ||
              (rawErrors?.length ? rawErrors[0] : undefined) ||
              (helperText as string) ||
              description
            }
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <GroupWork fontSize="small" />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};
